import React from 'react';
import { getMessage } from 'util/locale';
import COLORS from 'constants/colors';
import { isEmpty, chain } from 'lodash';

// util
import { getProductRateDisplayValuesFromBins } from 'util/formatters/data-viz/product';
import { getProductRateUnitSymbol } from 'util/unit';

import Legend from 'mapping/legends/Legend';
import EditLegendContainer from './EditLegendContainer';
import { applicationTypes } from 'constants/scripts';
import { useAppSelector } from 'app/hooks';
import { getActiveProductRateLegend, getActiveScriptId } from 'current/selectors';
import { getApplicationTypeForScriptType, getScriptById, getScriptTypeForScript } from 'scripts/selectors';
import { getFormatFromZones } from 'products/selectors';
import { Bins } from 'store/types/types';

type ApplicationType = typeof applicationTypes[keyof typeof applicationTypes]
type LegendOptions = {
  bins: Bins;
}

interface ProductRateLegendProps {
  bins?: Bins;
  className?: string;
  isEditable?: boolean;
  productType?: Omit<ApplicationType, typeof applicationTypes.APPLICATIONV2 | typeof applicationTypes.STABILIZER>;
  toggleEditLegend?: () => void;
}

const baseString = 'vra-product-rate-legend-container';
const ProductRateLegend = ({
  bins,
  // USDEV2-92: Disable Edit Product Rate Legends until a further enhancement is made
  isEditable = false,
  productType,
  toggleEditLegend,
  className
}: ProductRateLegendProps) => {
  const legend: LegendOptions = useAppSelector(getActiveProductRateLegend);
  const scriptId = useAppSelector(getActiveScriptId);
  const script = useAppSelector((state) => getScriptById(state, scriptId));
  if (isEmpty(legend) && !bins) {
    return null;
  }

  const currentScriptType = getScriptTypeForScript(script);
  const productFormat = script?.zones ? getFormatFromZones(script.zones) : '';
  const applicationType = productType || getApplicationTypeForScriptType(currentScriptType);
  const binsTuples = bins || legend.bins;
  const displayValues = getProductRateDisplayValuesFromBins({ applicationType, bins: binsTuples }).reverse();
  const displayColors = (COLORS.rate[applicationType].slice(0)).reverse();
  const lowLabel = getProductRateUnitSymbol(applicationType, productFormat);
  const title = applicationType === applicationTypes.SEED
    ? getMessage('rate')
    : getMessage('text__application-rate--product-rate');

  return (
    <section className={`${className} ${baseString}`} data-test-id='product-rate-legend'>
      <div className={`${baseString}__legend-wrapper`}>
        <EditLegendContainer
          maxValue={chain(binsTuples).last().min().value()}
          minValue={chain(binsTuples).first().max().value()}
          steps={binsTuples.length} />
        <Legend
          className={`${baseString}__product`}
          colors={displayColors}
          displayValues={displayValues}
          isEditable={isEditable}
          lowLabel={lowLabel}
          title={title}
          toggleEditLegend={toggleEditLegend}
        />
      </div>
    </section>
  );
};

export default ProductRateLegend;
